import { type GatsbyCustomImage, type ResolvedLink } from "@ts/components";
import { clsx } from "clsx";
import React, { type ReactNode } from "react";
import { Image } from "./Image/Image";

type ImageContentProps = {
  children?: ReactNode;
  image?: GatsbyCustomImage;
  images?: GatsbyCustomImage[];
  imageLink?: ResolvedLink;
  mobileHidden?: boolean;
  mobileReverse?: boolean;
  onClickImage?: (imageLink?: ResolvedLink) => void;
  oneThird?: boolean;
  reverse?: boolean;
  width?: string;
};

const Wrapper = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "flex w-full",
      props.reverse ? "md:flex-row-reverse" : "md:flex-row",
      props.mobileReverse ? "flex-col-reverse" : "flex-col",
    )}
  />
);

const ImageWrapper = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "relative w-full",
      props.mobileHidden && "hidden md:block",
      props.width === "225" && "h-peta w-full md:h-auto md:w-[220px]",
      props.width === "300" && "h-peta w-full md:h-auto md:w-[300px]",
      !props.width && props.oneThird && "md:w-2/5",
      !props.width && !props.oneThird && "flex-1 md:w-1/2 md:min-w-1/2",
    )}
  />
);

const ImageInner = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "inset-0 absolute flex flex-row md:flex-col",
    )}
  />
);

const ImageGrid = (props: any) => (
  <div {...props} className={clsx(props.className, "w-full md:h-1/2")} />
);

const ContentWrapper = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "w-full",
      props.width === "225" && "w-full md:w-[420px]",
      props.width === "300" && "w-full md:w-auto",
      !props.width && props.oneThird && "md:w-3/5",
      !props.width && !props.oneThird && "flex-1 md:w-1/2 md:min-w-1/2",
    )}
  />
);

const ImageContent = ({
  image,
  images,
  children,
  reverse,
  mobileReverse,
  mobileHidden,
  oneThird,
  width,
  imageLink,
  onClickImage,
}: ImageContentProps) => {
  return (
    <Wrapper reverse={reverse} mobileReverse={mobileReverse}>
      {image ? (
        <ImageWrapper
          mobileHidden={mobileHidden}
          oneThird={oneThird}
          width={width}
        >
          <Image
            image={image}
            onClick={onClickImage}
            imageLink={imageLink}
            alt={image?.alt}
            fullHeight
          />
        </ImageWrapper>
      ) : images ? (
        <ImageWrapper
          mobileHidden={mobileHidden}
          oneThird={oneThird}
          width={width}
        >
          <ImageInner>
            {images.map((img, index) => (
              <ImageGrid key={`${index}_${img?.alt}`}>
                <Image image={img} fullHeight />
              </ImageGrid>
            ))}
          </ImageInner>
        </ImageWrapper>
      ) : null}
      <ContentWrapper oneThird={oneThird} width={width}>
        {children}
      </ContentWrapper>
    </Wrapper>
  );
};

export default ImageContent;
